import { type FC, forwardRef } from 'react';
import { clsx } from '@team/clsx';
import { ReactKit } from '@team/react';
import { RootComponent } from '../../services/root-component';
import type {
  ActionProps,
  ContainerProps,
  DescriptionProps,
  HeaderProps,
  IconProps,
  Props,
} from './types';
import { Title } from '../../typography/title';
import { Text } from '../../typography/text';
import { Size } from './enums';
import css from './style.module.css';

export const Root = forwardRef<HTMLDivElement, Props>(function Component(
  { icon, header, children, action, ...props },
  forwardedRef
) {
  return (
    <Container {...props} ref={forwardedRef}>
      {ReactKit.hasNode(icon) && <Icon>{icon}</Icon>}

      {ReactKit.hasNode(header) && <Header>{header}</Header>}

      {ReactKit.hasNode(children) && <Description>{children}</Description>}

      {ReactKit.hasNode(action) && <Action>{action}</Action>}
    </Container>
  );
});

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  function Component({ size = Size.LARGE, ...props }, forwardedRef) {
    return (
      <RootComponent
        {...props}
        Component="div"
        baseClassName={clsx(
          css.root,
          {
            [Size.SMALL]: css.small,
            [Size.LARGE]: css.large,
            [Size.NONE]: undefined,
          }[size]
        )}
        ref={forwardedRef}
      />
    );
  }
);

export const Icon: FC<IconProps> = ({ className, ...props }) => (
  <div {...props} className={clsx(className, css.icon)} />
);

export const Header: FC<HeaderProps> = ({ className, children, ...props }) => {
  if (ReactKit.isPrimitiveNode(children)) {
    return (
      <Title
        {...props}
        level={3}
        weight={2}
        className={clsx(className, css.header)}
      >
        {children}
      </Title>
    );
  }

  return (
    <div {...props} className={clsx(className, css.header)}>
      {children}
    </div>
  );
};

export const Description: FC<DescriptionProps> = ({
  className,
  children,
  ...props
}) => {
  if (ReactKit.isPrimitiveNode(children)) {
    return (
      <Text {...props} weight={3} className={clsx(className, css.text)}>
        {children}
      </Text>
    );
  }

  return (
    <div {...props} className={clsx(className, css.text)}>
      {children}
    </div>
  );
};

export const Action: FC<ActionProps> = ({ className, ...props }) => (
  <div {...props} className={clsx(className, css.action)} />
);
